import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign,
  FileText,
  User,
  Calendar,
  CheckCircle,
  Clock,
  TrendingUp,
  Activity,
  Edit,
} from 'lucide-react';
import apiService from '../../services/api';
import { toast } from 'react-hot-toast';
import '../../styles/responsive-admin.css';

const AccountsManager = ({ user }) => {
  const [accountsData, setAccountsData] = useState({ tasks: [], enquiries: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks');
  const [editingTask, setEditingTask] = useState(null);

  useEffect(() => {
    fetchAccountsRecords();
  }, []);

  const fetchAccountsRecords = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAccountsRecords();
      setAccountsData(response || { tasks: [], enquiries: [] });
    } catch (error) {
      toast.error('Failed to fetch accounts records');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent_to_accounts':
        return '#2196f3';
      case 'completed':
        return '#4caf50';
      case 'in_progress':
        return '#ff9800';
      case 'registered':
        return '#9c27b0';
      default:
        return '#9e9e9e';
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await apiService.updateTaskStatus(taskId, newStatus);
      toast.success('Task status updated');
      fetchAccountsRecords();
      setEditingTask(null);
    } catch (error) {
      toast.error('Failed to update task status');
      console.error('Update error:', error);
    }
  };

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
      >
        <div>Loading accounts records...</div>
      </div>
    );
  }

  const totalRecords = accountsData.tasks.length + accountsData.enquiries.length;

  return (
    <div className='responsive-main'>
      <div className='responsive-header'>
        <div>
          <h1>Accounts Manager</h1>
          <p
            style={{ margin: '8px 0 0 0', color: 'var(--gray-600)', fontSize: 'var(--text-base)' }}
          >
            Manage verified records sent to accounts
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            color: 'var(--primary)',
          }}
        >
          <Activity size={20} />
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: '500' }}>Live Updates</span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className='responsive-grid cols-3' style={{ marginBottom: 'var(--space-8)' }}>
        <motion.div className='responsive-card' whileHover={{ y: -4, scale: 1.02 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: 'var(--radius-xl)',
                background: 'linear-gradient(135deg, var(--info) 0%, #1976d2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)',
              }}
            >
              <TrendingUp size={28} color='white' />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: 'var(--text-3xl)',
                  fontWeight: '700',
                  color: 'var(--gray-900)',
                }}
              >
                {totalRecords}
              </h3>
              <p
                style={{
                  margin: 0,
                  color: 'var(--gray-600)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: '500',
                }}
              >
                Total Records
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div className='responsive-card' whileHover={{ y: -4, scale: 1.02 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: 'var(--radius-xl)',
                background: 'linear-gradient(135deg, var(--warning) 0%, #f57c00 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 25px rgba(245, 158, 11, 0.3)',
              }}
            >
              <CheckCircle size={28} color='white' />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: 'var(--text-3xl)',
                  fontWeight: '700',
                  color: 'var(--gray-900)',
                }}
              >
                {accountsData.tasks.length}
              </h3>
              <p
                style={{
                  margin: 0,
                  color: 'var(--gray-600)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: '500',
                }}
              >
                Tasks
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div className='responsive-card' whileHover={{ y: -4, scale: 1.02 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: 'var(--radius-xl)',
                background: 'linear-gradient(135deg, var(--success) 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
              }}
            >
              <User size={28} color='white' />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: 'var(--text-3xl)',
                  fontWeight: '700',
                  color: 'var(--gray-900)',
                }}
              >
                {accountsData.enquiries.length}
              </h3>
              <p
                style={{
                  margin: 0,
                  color: 'var(--gray-600)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: '500',
                }}
              >
                Enquiries
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div
        className='responsive-card'
        style={{ marginBottom: 'var(--space-8)', padding: 'var(--space-2)' }}
      >
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`responsive-btn ${activeTab === 'tasks' ? 'responsive-btn-primary' : 'responsive-btn-secondary'}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              borderRadius: 'var(--radius-lg)',
              transition: 'all var(--transition-base)',
            }}
          >
            <CheckCircle size={18} />
            Tasks ({accountsData.tasks.length})
          </button>
          <button
            onClick={() => setActiveTab('enquiries')}
            className={`responsive-btn ${activeTab === 'enquiries' ? 'responsive-btn-primary' : 'responsive-btn-secondary'}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              borderRadius: 'var(--radius-lg)',
              transition: 'all var(--transition-base)',
            }}
          >
            <User size={18} />
            Enquiries ({accountsData.enquiries.length})
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'tasks' && (
        <div>
          {accountsData.tasks.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e0e0e0',
              }}
            >
              <CheckCircle size={48} style={{ color: '#ccc', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#666' }}>No tasks in accounts</h3>
              <p style={{ margin: 0, color: '#999' }}>
                Tasks will appear here after document verification
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {accountsData.tasks.map((task) => (
                <motion.div
                  key={task.id}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                  whileHover={{ y: -2 }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
                        Task #{task.id}
                      </h3>
                      <div
                        style={{
                          display: 'flex',
                          gap: '16px',
                          fontSize: '14px',
                          color: '#666',
                          marginBottom: '8px',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar size={14} />
                          {formatDate(task.created_at)}
                        </span>
                        {task.AssignedTo && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <User size={14} />
                            {task.AssignedTo.name}
                          </span>
                        )}
                      </div>
                      {task.task_details && (
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {Object.entries(task.task_details).map(([key, value]) => (
                            <div key={key} style={{ marginBottom: '4px' }}>
                              <strong>{key}:</strong> {value}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      {editingTask === task.id ? (
                        <select
                          value={task.status}
                          onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                          className='responsive-form-input responsive-form-select'
                          style={{ minWidth: '140px', fontSize: 'var(--text-xs)' }}
                        >
                          <option value='sent_to_accounts'>Sent to Accounts</option>
                          <option value='in_progress'>In Progress</option>
                          <option value='completed'>Completed</option>
                          <option value='registered'>Registered</option>
                        </select>
                      ) : (
                        <>
                          <span
                            style={{
                              background: getStatusColor(task.status),
                              color: 'white',
                              padding: 'var(--space-1) var(--space-3)',
                              borderRadius: 'var(--radius-full)',
                              fontSize: 'var(--text-xs)',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-1)',
                            }}
                          >
                            <Clock size={12} />
                            {task.status}
                          </span>
                          <button
                            onClick={() => setEditingTask(task.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 'var(--space-1)',
                              borderRadius: 'var(--radius)',
                              color: 'var(--gray-500)',
                              transition: 'all var(--transition-base)',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = 'var(--primary)')}
                            onMouseLeave={(e) => (e.target.style.color = 'var(--gray-500)')}
                          >
                            <Edit size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'enquiries' && (
        <div>
          {accountsData.enquiries.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e0e0e0',
              }}
            >
              <User size={48} style={{ color: '#ccc', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#666' }}>No enquiries in accounts</h3>
              <p style={{ margin: 0, color: '#999' }}>
                Enquiries will appear here after document verification
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {accountsData.enquiries.map((enquiry) => (
                <motion.div
                  key={enquiry.id}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                  whileHover={{ y: -2 }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
                        {enquiry.name}
                      </h3>
                      <div
                        style={{
                          display: 'flex',
                          gap: '16px',
                          fontSize: '14px',
                          color: '#666',
                          marginBottom: '8px',
                        }}
                      >
                        <span>{enquiry.email}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar size={14} />
                          {formatDate(enquiry.created_at)}
                        </span>
                      </div>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        <strong>Subject:</strong> {enquiry.subject}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          background: getStatusColor('sent_to_accounts'),
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <DollarSign size={12} />
                        In Accounts
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccountsManager;
